import React from 'react'
import { Alert, Descriptions, Spin, Tag, Typography } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { MenuInfo } from 'rc-menu/lib/interface'
import moment from 'moment'
import { horsesApi } from 'api/horses'
import { HorseForm } from 'components/horses/HorseForm/HorseForm'
import { RejectHorseModal } from 'components/horses/RejectHorseModal/RejectHorseModal'
import { PageWrapper } from 'components/ui'
import { CardLink } from 'components/ui/CardLink/CardLink'
import { StatusDropdown } from 'components/ui/StatusDropdown/StatusDropdown'
import { EHorseStatus, IPopulatedHorseWithoutCount } from 'models/horse'
import { getHorseStatusItems, getHorseStatusView } from 'shared/constants/horses'
import { superAdminRoutes } from 'shared/constants/routes'
import { getUserEditPath } from 'routing/utils/getUserEditPath'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import { getHorseDaysLeft } from 'shared/utils/getHorseDaysLeft'
import { phoneMaskStatic } from 'shared/utils/phoneMaskStatic'
import { showServerError } from 'shared/utils/showServerError'
import { useAppDispatch } from 'shared/hooks/useReduxHooks'
import { getModerationHorsesCount } from 'store/horses'

export const Horse: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [horse, setHorse] = React.useState<IPopulatedHorseWithoutCount | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [openRejectModal, setOpenRejectModal] = React.useState(false)

  const getHorse = async () => {
    if (!id) return
    try {
      setIsLoading(true)
      const response = await horsesApi.getHorseForManage(id)
      setHorse(response.data)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    getHorse()
  }, [id])

  const onCloseRejectModal = () => {
    setOpenRejectModal(false)
    bodyScrollLock.disable()
  }

  const onChangeStatus = async (status: EHorseStatus, rejectReason?: string) => {
    if (!id) return
    try {
      await horsesApi.changeHorseStatus(id, { status, reason_of_cancel: rejectReason })
      dispatch(getModerationHorsesCount())
      getHorse()
    } catch (e) {
      showServerError(e)
    }
  }

  const onStatusTagClick = (e: MenuInfo) => {
    if (e.key === EHorseStatus.REJECTED) {
      bodyScrollLock.enable()
      return setOpenRejectModal(true)
    }
    onChangeStatus(e.key as EHorseStatus)
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size='large' />
      </div>
    )
  }

  if (!horse) return null

  const daysLeft = getHorseDaysLeft(horse.published_until)
  const statusView = getHorseStatusView(horse)

  return (
    <>
      <PageWrapper>
        <Typography.Link
          onClick={() => navigate(superAdminRoutes.HORSES)}
          style={{ display: 'inline-block', marginBottom: 8 }}
        >
          <ArrowLeftOutlined /> Назад к лошадям
        </Typography.Link>
        <Typography.Title level={4}>
          №{horse.number} {horse.name}
        </Typography.Title>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <StatusDropdown
            items={getHorseStatusItems(horse.status)}
            label={statusView.label}
            color={statusView.color}
            onClick={onStatusTagClick}
          />
          {horse.status === EHorseStatus.PUBLISHED && daysLeft !== null && (
            <Tag color={daysLeft > 7 ? 'blue' : 'orange'}>
              Осталось дней: {daysLeft}
            </Tag>
          )}
        </div>

        {horse.reason_of_cancel && (
          <Alert
            type='error'
            showIcon
            message='Причина отклонения'
            description={horse.reason_of_cancel}
            style={{ marginBottom: 16 }}
          />
        )}

        {horse.status === EHorseStatus.ARCHIVED && (
          <Alert
            type='info'
            showIcon
            message={statusView.note ? `${statusView.label} — ${statusView.note}` : statusView.label}
            description='Опубликовать снова можно через смену статуса'
            style={{ marginBottom: 16 }}
          />
        )}

        <Descriptions bordered size='small' column={2} style={{ marginBottom: 24 }}>
          <Descriptions.Item label='Продавец'>
            {/* Соседней вкладкой, чтобы не потерять несохранённую анкету лошади */}
            {horse.user_id?._id ? (
              <CardLink to={getUserEditPath(horse.user_id._id)} newTab>
                {horse.user_id.full_name || 'ФИО не заполнено'}
              </CardLink>
            ) : (
              'Удаленный аккаунт'
            )}
          </Descriptions.Item>
          <Descriptions.Item label='Телефон продавца'>
            {horse.user_id?.tel ? phoneMaskStatic(horse.user_id.tel) : '—'}
          </Descriptions.Item>
          <Descriptions.Item label='Создана'>
            {moment(horse.createdAt).format('DD.MM.YYYY')}
          </Descriptions.Item>
          <Descriptions.Item label='Опубликована до'>
            {horse.published_until
              ? moment(horse.published_until).format('DD.MM.YYYY')
              : '—'}
          </Descriptions.Item>
        </Descriptions>

        <HorseForm horse={horse} onSaved={getHorse} />
      </PageWrapper>
      <RejectHorseModal
        isOpen={openRejectModal}
        onSuccess={(reason) => onChangeStatus(EHorseStatus.REJECTED, reason)}
        close={onCloseRejectModal}
      />
    </>
  )
}
